import SEO from '@/components/base/SEO';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { useState } from 'react';

const aboutJsonLd = [
  {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于吃瓜视频 - 品牌介绍与联系方式',
    description: '吃瓜视频成立于2023年，是国内领先的娱乐八卦内容聚合平台。致力于为用户提供最新、最全、最热的吃瓜视频内容。',
    url: 'https://nexkinn.cn/about',
    isPartOf: { '@type': 'WebSite', name: '吃瓜视频', url: 'https://nexkinn.cn' },
    inLanguage: 'zh-CN',
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '吃瓜视频网站是如何运营的？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '吃瓜视频拥有一支专业的内容运营团队，7x24小时实时追踪全网热门话题。我们通过多渠道信息采集和人工审核机制，确保内容的真实性、时效性和丰富性。',
        },
      },
      {
        '@type': 'Question',
        name: '如何联系吃瓜视频团队？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '您可以通过以下方式联系我们：邮箱 contact@nexkinn.cn，或者在关于我们页面填写联系表单。商务合作请发送邮件至 biz@nexkinn.cn，我们会在24小时内回复。',
        },
      },
      {
        '@type': 'Question',
        name: '吃瓜视频的内容来源是否可靠？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '吃瓜视频始终坚持内容真实性原则。我们的编辑团队会对每一条内容进行多方核实，确保信息来源可靠。同时我们欢迎用户通过举报渠道反馈不实信息。',
        },
      },
      {
        '@type': 'Question',
        name: '如何在吃瓜视频上观看完整版视频？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '用户只需访问吃瓜视频官网 nexkinn.cn，即可在线观看所有内容。网站支持高清播放，无需注册和下载。您也可以通过分类浏览或搜索功能快速找到感兴趣的内容。',
        },
      },
    ],
  },
];

export default function About() {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const honeyInput = (e.currentTarget as HTMLFormElement).querySelector('input[name="website_alt"]') as HTMLInputElement;
    if (honeyInput && honeyInput.value.trim()) {
      setFormStatus('success');
      setFormMessage('感谢您的留言，我们会尽快回复！');
      return;
    }
    setFormStatus('loading');
    try {
      const formEl = e.currentTarget as HTMLFormElement;
      const fd = new FormData(formEl);
      fd.delete('website_alt');
      const searchParams = new URLSearchParams();
      fd.forEach((v, k) => searchParams.append(k, v.toString()));

      const resp = await fetch('https://form.readdy.ai/api/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: searchParams.toString(),
      });
      const text = await resp.text();
      let parsed;
      try { parsed = JSON.parse(text); } catch { parsed = null; }
      if (resp.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('感谢您的留言，我们会尽快回复！');
        setFormData({ name: '', email: '', message: '' });
      } else {
        setFormStatus('error');
        setFormMessage(parsed?.meta?.message || '提交失败，请稍后重试');
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请检查后重试');
    }
  };

  return (
    <>
      <SEO
        title="关于我们 - 吃瓜视频 品牌介绍与联系方式"
        description="吃瓜视频成立于2023年，是国内领先的娱乐八卦内容聚合平台。了解吃瓜视频的品牌故事、平台优势和联系方式。每日更新网红黑料、明星八卦、国产吃瓜视频在线观看完整版延禧攻略。"
        keywords="吃瓜视频官网, 吃瓜视频入口, 吃瓜视频app, 吃瓜视频下载, 吃瓜视频最新地址, 吃瓜视频在线"
        canonicalUrl="/about"
        jsonLd={aboutJsonLd}
      />
      <Navbar />
      <main className="pt-16 bg-background-50">
        <Breadcrumb items={[{ label: '首页', path: '/' }, { label: '关于我们' }]} />

        <section className="relative w-full h-[300px] md:h-[400px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=A%20modern%20media%20company%20office%20with%20multiple%20large%20screens%20displaying%20social%20media%20dashboards%20and%20video%20content%2C%20warm%20amber%20and%20golden%20lighting%2C%20professional%20team%20atmosphere%2C%20editorial%20photography%2C%20wide%20shot%20of%20creative%20workspace&width=1920&height=800&seq=chigua-about-hero&orientation=landscape"
            alt="吃瓜视频团队 - 专业内容运营平台"
            title="吃瓜视频 - 品牌介绍"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60"></div>
          <div className="relative z-10 w-full h-full flex flex-col items-center justify-center text-center px-4">
            <h1 className="text-2xl md:text-4xl font-heading font-bold text-white mb-3">关于我们</h1>
            <p className="text-sm md:text-base text-white/70 max-w-lg">了解吃瓜视频的品牌故事与使命</p>
          </div>
        </section>

        <section className="py-14 md:py-20 px-4 md:px-6" id="brand">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-10 lg:gap-16 items-start">
              <div className="lg:w-[42%]">
                <span className="inline-block px-3 py-1 rounded-full border border-background-300 text-xs text-foreground-500 mb-5">
                  品牌故事
                </span>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-4 leading-tight">
                  🍉 你身边的
                  <br />
                  <span className="text-foreground-600 font-normal">吃瓜第一站</span>
                </h2>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-5">
                  吃瓜视频成立于2023年，是国内领先的娱乐八卦内容聚合平台。我们致力于为用户提供最新、最全、最热的吃瓜视频内容，涵盖网红黑料追踪、明星八卦爆料、热门剧集回顾等多个领域。
                </p>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-6">
                  团队由资深的娱乐媒体从业者和技术专家组成，坚持每日更新的内容策略，确保每一位吃瓜群众都能第一时间获取最新资讯。从网红塌房现场到经典国产剧集回顾，从实时热点追踪到深度内容整合——吃瓜视频，让你的八卦雷达永不掉线。
                </p>
                <div className="flex flex-wrap gap-2">
                  {['真实可靠', '更新迅速', '内容全面', '用户体验至上'].map((v) => (
                    <span key={v} className="px-3 py-1 rounded-full bg-primary-50 text-primary-700 text-xs font-medium whitespace-nowrap">
                      {v}
                    </span>
                  ))}
                </div>
              </div>

              <div className="lg:w-[58%]">
                <div className="flex flex-wrap gap-8 md:gap-12 mb-8 justify-end">
                  {[
                    { num: '580万+', label: '日访问量' },
                    { num: '1.2万+', label: '视频总量' },
                    { num: '230万+', label: '活跃用户' },
                  ].map((stat) => (
                    <div key={stat.label} className="text-right">
                      <div className="text-2xl md:text-3xl font-bold text-foreground-950">{stat.num}</div>
                      <div className="text-xs text-foreground-500 mt-1">{stat.label}</div>
                    </div>
                  ))}
                </div>
                <div className="relative h-[320px] md:h-[380px]">
                  <div className="absolute top-0 left-0 w-[55%] h-[70%] rounded-xl overflow-hidden bg-background-200">
                    <img
                      src="https://readdy.ai/api/search-image?query=A%20creative%20media%20team%20brainstorming%20session%20with%20sticky%20notes%20on%20glass%20wall%2C%20warm%20natural%20lighting%2C%20modern%20office%20setting%2C%20editorial%20photography%2C%20authentic%20team%20interaction%2C%20professional%20yet%20relaxed%20vibe&width=600&height=800&seq=chigua-about-team-2&orientation=portrait"
                      alt="吃瓜视频团队工作场景"
                      title="吃瓜视频专业内容团队"
                      className="w-full h-full object-cover object-top"
                      loading="lazy"
                    />
                  </div>
                  <div className="absolute bottom-0 right-0 w-[55%] h-[75%] rounded-xl overflow-hidden bg-background-200">
                    <img
                      src="https://readdy.ai/api/search-image?query=A%20cozy%20content%20creation%20studio%20with%20multiple%20monitors%20displaying%20social%20feeds%20and%20video%20editing%20software%2C%20warm%20ambient%20lighting%2C%20modern%20creative%20workspace%2C%20editorial%20photography%2C%20plants%20and%20art%20on%20walls&width=600&height=800&seq=chigua-about-office-2&orientation=portrait"
                      alt="吃瓜视频工作室环境"
                      title="吃瓜视频编辑工作室"
                      className="w-full h-full object-cover object-top"
                      loading="lazy"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 text-center mb-12">
              我们的优势
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { icon: 'ri-flashlight-line', title: '实时追踪', desc: '7x24小时不间断监控全网热点，第一时间推送最新吃瓜内容' },
                { icon: 'ri-shield-check-line', title: '真实可靠', desc: '编辑团队多方核实信息来源，确保每一条内容的真实性' },
                { icon: 'ri-stack-line', title: '内容全面', desc: '覆盖网红、明星、剧集等多个领域，满足不同吃瓜需求' },
                { icon: 'ri-speed-up-line', title: '流畅体验', desc: '高清画质极速加载，让每一次吃瓜都畅快淋漓' },
              ].map((item) => (
                <div key={item.title} className="p-6 rounded-xl bg-background-50 text-center">
                  <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-full bg-accent-100">
                    <i className={`${item.icon} text-accent-600 text-xl`}></i>
                  </div>
                  <h3 className="text-base font-heading font-bold text-foreground-950 mb-2">{item.title}</h3>
                  <p className="text-xs text-foreground-500 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
              （ 媒体评价 ）
            </h2>
            <p className="text-sm text-foreground-500 mb-8">来自行业与用户的声音</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 text-left">
              {[
                { quote: '吃瓜视频已成为国内娱乐八卦领域的重要信息源，其内容更新速度和广度在同类平台中表现突出。', source: '《新媒体观察》', rating: 4.8 },
                { quote: '作为新兴的内容聚合平台，吃瓜视频凭借精准的内容定位和优质的用户体验，迅速积累了大量忠实用户。', source: '《互联网周刊》', rating: 4.7 },
              ].map((item, idx) => (
                <div key={idx} className="p-5 rounded-xl bg-background-100">
                  <div className="flex items-center gap-1 mb-3">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className={`ri-star-fill text-xs ${i < Math.floor(item.rating) ? 'text-primary-500' : 'text-background-300'}`}></i>
                    ))}
                    <span className="text-xs font-semibold text-foreground-700 ml-1">{item.rating}</span>
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-3 italic">&ldquo;{item.quote}&rdquo;</p>
                  <span className="text-xs text-foreground-500 font-medium">—— {item.source}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100" id="contact">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-10 lg:gap-16">
              <div className="lg:w-[45%]">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-4">联系我们</h2>
                <p className="text-sm text-foreground-600 leading-relaxed mb-6">
                  无论您是商务合作、内容投稿，还是有任何疑问或建议，都欢迎通过以下方式与我们取得联系。我们会在24小时内回复。
                </p>
                <div className="flex flex-col gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 flex-shrink-0">
                      <i className="ri-mail-line text-accent-600"></i>
                    </div>
                    <div>
                      <div className="text-xs text-foreground-500">商务合作</div>
                      <div className="text-sm font-medium text-foreground-800">biz@nexkinn.cn</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 flex-shrink-0">
                      <i className="ri-customer-service-line text-primary-600"></i>
                    </div>
                    <div>
                      <div className="text-xs text-foreground-500">用户支持</div>
                      <div className="text-sm font-medium text-foreground-800">contact@nexkinn.cn</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-secondary-100 flex-shrink-0">
                      <i className="ri-map-pin-line text-secondary-600"></i>
                    </div>
                    <div>
                      <div className="text-xs text-foreground-500">公司地址</div>
                      <div className="text-sm font-medium text-foreground-800">中国北京市朝阳区互联网科技园</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="lg:w-[55%]">
                <form onSubmit={handleSubmit} className="p-6 rounded-xl bg-background-50" data-readdy-form="">
                  <h3 className="text-lg font-heading font-bold text-foreground-950 mb-5">发送留言</h3>
                  <div className="flex flex-col gap-4">
                    <div>
                      <label className="block text-xs font-medium text-foreground-700 mb-1.5">姓名</label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                        className="w-full h-10 px-3 rounded-lg border border-background-300 bg-transparent text-sm text-foreground-900 placeholder-foreground-400 outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的姓名"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-foreground-700 mb-1.5">邮箱</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
                        className="w-full h-10 px-3 rounded-lg border border-background-300 bg-transparent text-sm text-foreground-900 placeholder-foreground-400 outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的邮箱"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-foreground-700 mb-1.5">留言内容</label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={(e) => setFormData((prev) => ({ ...prev, message: e.target.value }))}
                        rows={4}
                        maxLength={500}
                        className="w-full px-3 py-2 rounded-lg border border-background-300 bg-transparent text-sm text-foreground-900 placeholder-foreground-400 outline-none focus:border-primary-400 transition-colors resize-none"
                        placeholder="请输入您的留言（500字以内）"
                        required
                      ></textarea>
                      <p className="text-xs text-foreground-400 mt-1">{formData.message.length}/500</p>
                    </div>
                    <input
                      type="text"
                      name="website_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="fixed left-[-9999px] opacity-0 h-0 w-0 pointer-events-none"
                    />
                    <button
                      type="submit"
                      disabled={formStatus === 'loading'}
                      className="w-full h-11 rounded-lg bg-primary-500 hover:bg-primary-600 text-white font-medium text-sm transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
                    >
                      {formStatus === 'loading' ? (
                        <span className="flex items-center justify-center gap-2">
                          <i className="ri-loader-4-line animate-spin"></i> 提交中...
                        </span>
                      ) : '提交留言'}
                    </button>
                    {formStatus === 'success' && (
                      <p className="text-sm text-green-600 text-center">{formMessage}</p>
                    )}
                    {formStatus === 'error' && (
                      <p className="text-sm text-red-500 text-center">{formMessage}</p>
                    )}
                  </div>
                </form>
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50" id="faq">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 text-center mb-10">
              常见问题
            </h2>
            <div className="flex flex-col gap-3">
              {[
                { q: '吃瓜视频网站是如何运营的？', a: '吃瓜视频拥有一支专业的内容运营团队，7x24小时实时追踪全网热门话题。我们通过多渠道信息采集和人工审核机制，确保内容的真实性、时效性和丰富性。' },
                { q: '如何联系吃瓜视频团队？', a: '您可以通过邮箱 contact@nexkinn.cn 联系我们，或在关于我们页面填写联系表单。商务合作请发送邮件至 biz@nexkinn.cn。' },
                { q: '吃瓜视频的内容来源是否可靠？', a: '吃瓜视频始终坚持内容真实性原则。我们的编辑团队会对每一条内容进行多方核实，确保信息来源可靠。同时我们欢迎用户通过举报渠道反馈不实信息。' },
                { q: '如何在吃瓜视频上观看完整版视频？', a: '用户只需访问吃瓜视频官网即可在线观看所有内容。网站支持高清播放，无需注册和下载。您也可以通过分类浏览或搜索功能快速找到感兴趣的内容。' },
              ].map((faq, idx) => (
                <details key={idx} className="group rounded-xl bg-background-100 border border-background-200/70">
                  <summary className="flex items-center justify-between px-5 py-4 cursor-pointer list-none">
                    <span className="text-sm font-medium text-foreground-800 pr-4">{faq.q}</span>
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform flex-shrink-0"></i>
                  </summary>
                  <div className="px-5 pb-4">
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50" id="app">
          <div className="max-w-3xl mx-auto text-center">
            <div className="mb-8 max-w-xl mx-auto">
              <div className="relative rounded-2xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=A%20smartphone%20displaying%20a%20modern%20social%20media%20app%20interface%20with%20entertainment%20content%2C%20warm%20golden%20and%20amber%20ambient%20lighting%2C%20editorial%20product%20photography%2C%20clean%20minimal%20background&width=1200&height=600&seq=chigua-about-app&orientation=landscape"
                  alt="吃瓜视频APP"
                  title="吃瓜视频移动端体验"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-black/30 via-transparent to-black/30"></div>
              </div>
            </div>
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-4">
              加入<span className="italic text-primary-600 mx-1">吃瓜</span>大家庭
            </h2>
            <p className="text-sm text-foreground-500 mb-8">每日更新 · 实时追踪 · 高清流畅 · 免费观看</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href="#"
                className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-foreground-950 hover:bg-foreground-800 text-white font-medium text-sm transition-all cursor-pointer whitespace-nowrap"
                rel="nofollow"
              >
                <i className="ri-apple-fill"></i>
                iOS 下载
              </a>
              <a
                href="#"
                className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full border border-foreground-300 text-foreground-700 hover:text-foreground-950 hover:border-foreground-500 font-medium text-sm transition-all cursor-pointer whitespace-nowrap"
                rel="nofollow"
              >
                <i className="ri-android-fill"></i>
                Android 下载
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}